"use client";

import CheckmarkWhiteIcon from "public/next-public/common/icons/checkmark-icon.svg";
import React from "react";
import styles from "./checkbox.module.scss";

interface CheckboxProps {
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  required?: boolean;
  size?: "small" | "medium";
  /** Accepts a plain string or rich JSX (e.g. a label containing a link). */
  label?: React.ReactNode;
  showLabel?: boolean;
  /**
   * Allow a long label to wrap onto multiple lines instead of forcing a single line.
   * Also top-aligns the box with the first line. Off by default to preserve the
   * single-line behavior relied on by filter/facet checkboxes.
   */
  wrapLabel?: boolean;
  name?: string;
  /** Forwarded to the native input — the value submitted with the form when checked. */
  value?: string;
  id?: string;
  className?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  "aria-label"?: string;
  "aria-labelledby"?: string;
}

const Checkbox: React.FC<CheckboxProps> = ({
  checked,
  defaultChecked,
  disabled = false,
  required = false,
  size = "small",
  label,
  showLabel = true,
  wrapLabel = false,
  name,
  value,
  id,
  className,
  onChange,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
}) => {
  if (process.env.NODE_ENV !== "production") {
    const hasVisibleLabel = showLabel && !!label;
    const hasStringLabelName = !showLabel && typeof label === "string" && !!label;
    if (!hasVisibleLabel && !hasStringLabelName && !ariaLabel && !ariaLabelledBy) {
      console.warn(
        "Checkbox: no accessible name. When the visible label is hidden or the label is not a plain string, supply aria-label or aria-labelledby.",
      );
    }
  }

  const resolvedAriaLabel = ariaLabelledBy
    ? undefined
    : ariaLabel || (!showLabel && typeof label === "string" ? label : undefined);

  const classNames = [
    styles.Checkbox,
    styles[size],
    disabled ? styles.disabled : "",
    wrapLabel ? styles.wrapLabel : "",
    className || "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <label className={classNames}>
      <input
        type="checkbox"
        className={styles.input}
        checked={checked}
        defaultChecked={defaultChecked}
        disabled={disabled}
        required={required}
        name={name}
        value={value}
        id={id}
        onChange={onChange}
        aria-label={resolvedAriaLabel}
        aria-labelledby={ariaLabelledBy}
      />
      <span className={styles.box}>
        <CheckmarkWhiteIcon className={styles.checkmark} aria-hidden="true" />
      </span>
      {showLabel && label && <span className={styles.label}>{label}</span>}
    </label>
  );
};

export default Checkbox;
